import React from "react";
import { Wallet, QrCode, Link as LinkIcon, CheckCircle2, Smartphone } from "lucide-react";
import Beam from "../illustrations/Beam";
import Tiles from "../illustrations/Tiles";

export default function Quickstart() {
  const steps = [
    { title: "Install Phantom", desc: "Download the Phantom wallet for iOS/Android or as a browser extension.", Icon: Smartphone },
    { title: "Open Payme.sol", desc: "Visit paymesol.app and choose Basic or Pro.", Icon: LinkIcon },
    { title: "Connect Wallet", desc: "Securely connect Phantom. We only read your public address.", Icon: Wallet },
    { title: "Generate QR", desc: "Enter amount and currency, then generate a QR for the payer to scan.", Icon: QrCode },
    { title: "Confirm", desc: "Customer scans, reviews, and confirms.", Icon: CheckCircle2 },
  ];

  return (
    <section id="quickstart" className="relative overflow-hidden" data-reveal>
      <div className="mx-auto max-w-4xl px-4 py-16 relative">
        <h2 className="mb-6 text-3xl font-semibold text-center">Quickstart</h2>
        <p className="mb-10 max-w-2xl text-neutral-300 text-center mx-auto">
          Get up and running in minutes. Follow these simple steps to start accepting payments on Solana with Payme.sol.
        </p>

        <div className="relative">
          <Tiles className="opacity-10" density={28} />

          {/* Timeline backbone */}
          <div className="pointer-events-none absolute left-[28px] top-0 hidden h-full w-px bg-gradient-to-b from-indigo-500/40 via-indigo-500/20 to-transparent md:block" />

          <ol className="relative mx-auto grid max-w-2xl grid-cols-1 gap-5">
            {steps.map(({ title, desc, Icon }, idx) => (
              <li
                key={title}
                data-reveal
                style={{ ['--reveal-delay' as any]: `${idx * 120}ms` }}
                className="group relative rounded-lg border border-neutral-700 bg-neutral-900/40 p-5 transition-all duration-300 hover:border-indigo-500/50 hover:bg-indigo-500/5 hover:shadow-[0_0_34px_rgba(99,102,241,0.12)] hover:-translate-y-0.5 md:pl-16"
              >
                {/* Node */}
                <div className="md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2 md:flex md:flex-col md:items-center md:justify-center">
                  <div className="relative mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600/20 text-indigo-300 md:mb-0">
                    <Beam tone="brandLight" className="absolute inset-0 -z-10" />
                    <span className="pointer-events-none absolute inset-0 rounded-full ps-pulse opacity-0 transition-opacity duration-300 group-hover:opacity-80" />
                    <span className="pointer-events-none absolute -top-2 -right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-[11px] font-semibold text-white shadow-sm md:static md:translate-x-0 md:translate-y-0">{idx + 1}</span>
                    <Icon className="size-4" />
                  </div>
                </div>

                {/* Content */}
                <div className="text-center md:text-left md:ml-2">
                  <h3 className="text-base font-semibold text-white">{title}</h3>
                  <p className="mt-1 text-sm text-neutral-300">{desc}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* CTA beneath timeline */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://basic.paymesol.app"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center justify-center rounded-md border border-neutral-700 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
            >
              Open Basic
            </a>
            <a
              href="https://pro.paymesol.app"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            >
              Open Pro
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
